/**
 * UserImageTransformProvider - Hybrid Mode
 *
 * Provider che gestisce gli stati temporanei delle trasformazioni UserImage
 * utilizzando SharedValues per performance ottimali senza re-render.
 *
 * NUOVO: Integrato con TransformEventBus per ricevere eventi da slider globali
 * - Ascolta eventi per activeUserImage.id corrente
 * - Processa eventi nel UI thread per performance ottimali
 * - Mantiene tutta la funzionalità esistente intatta
 *
 * Si sincronizza automaticamente con activeUserImageArea dal store globale.
 * Richiede responsiveScaleSV come prop per calcoli precisi nelle worklets.
 */
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useSharedValue, SharedValue, useAnimatedReaction } from 'react-native-reanimated';
import { observer } from 'mobx-react-lite';
import { UserImageTransformContext, UserImageTransformContextProps, TempPanState } from './UserImageTransformContext';
import { eventSharedValue } from './TransformEventBus';
import { IUserImageModel } from '../store/_models/userImage';

interface UserImageTransformProviderProps {
  children: ReactNode;
  responsiveScaleSV: SharedValue<number>;
  userImage: IUserImageModel; // userImage da monitorare
  editable: boolean;
  enableEventDebug?: boolean; // Opzionale per debugging eventi
}

/**
 * Provider per il context di trasformazioni UserImage - Modalità Hybrid
 *
 * Gestisce SharedValues per trasformazioni temporanee e si sincronizza
 * Utilizza responsiveScaleSV fornito come prop per calcoli precisi.
 *
 */
const UserImageTransformProvider = observer(
  ({ children, responsiveScaleSV, userImage, enableEventDebug = false, editable }: UserImageTransformProviderProps) => {
    const tempPanSV = useSharedValue<TempPanState | null>(null);
    const tempZoomSV = useSharedValue<number | null>(null);
    const tempRotationSV = useSharedValue<number | null>(null);

    // Timestamp di mount del provider per filtrare eventi passati
    const mountTimestamp = useRef(Date.now());

    // Reset degli SharedValues quando cambia l'userImage specifico
    useEffect(() => {
      tempPanSV.value = null;
      tempZoomSV.value = null;
      tempRotationSV.value = null;
      // Aggiorna timestamp quando cambia l'userImage
      mountTimestamp.current = Date.now();
    }, [userImage.id, tempPanSV, tempZoomSV, tempRotationSV]);

    /**
     * Reset di tutti gli stati temporanei
     * null indica stato di quiete (nessuna trasformazione temporanea attiva)
     */
    const resetTempState = useCallback(() => {
      tempPanSV.value = null;
      tempZoomSV.value = null;
      tempRotationSV.value = null;
    }, [tempPanSV, tempZoomSV, tempRotationSV]);

    // Event Listener per TransformEventBus (UI Thread)
    // Ascolta eventi per questo userImage specifico e aggiorna SharedValues
    useAnimatedReaction(
      () => {
        'worklet';
        return { event: eventSharedValue.value, responsiveScale: responsiveScaleSV.value };
      },
      ({ event, responsiveScale }) => {
        'worklet';

        // Ignora eventi creati prima del mount di questo provider
        if (!event || event.timestamp < mountTimestamp.current) {
          return;
        }

        // Filtra solo eventi per questo userImage specifico (props)
        if (event.userImageId !== userImage.id || !editable) {
          return;
        }

        // responsiveScale === 0 means the layout is not yet initialized or is tearing down
        if (responsiveScale === 0) {
          return;
        }

        // Debug log per eventi ricevuti
        if (__DEV__) {
          console.log('📡 [Provider] Event received:', {
            id: event.id,
            type: event.type,
            userImageId: event.userImageId.substring(0, 8) + '...',
            payload: event.payload,
            source: event.source,
            timestamp: event.timestamp,
            mountTimestamp: mountTimestamp.current,
          });
        }

        // Processa eventi in base al tipo
        switch (event.type) {
          case 'temp-zoom':
            // Aggiorna lo zoom temporaneo con valore assoluto
            // tempZoomSV.value = event.payload.absoluteScale;

            userImage.setScale(event.payload.absoluteScale, responsiveScale);
            break;

          case 'temp-rotation':
            // Calcola nuova rotazione e offset usando responsiveScaleSV
            // const { rotationDeg, offsetX, offsetY } = calculateNewRotationWorklet(event.payload.absoluteRotation);
            // // Aggiorna gli SharedValues temporanei con i valori calcolati
            // tempRotationSV.value = rotationDeg;
            // // Calcola gli offset temporanei come differenza dai valori attuali
            // tempPanSV.value = { x: offsetX, y: offsetY };

            userImage.setRotation(event.payload.absoluteRotation, responsiveScale);
            break;

          case 'temp-pan':
            // Aggiorna il pan temporaneo dalle coordinate ricevute
            // tempPanSV.value = { x: event.payload.x, y: event.payload.y };

            userImage.setOffsets({ x: event.payload.x, y: event.payload.y });
            break;

          case 'apply-zoom':
            userImage.applyScale(event.payload.absoluteScale, responsiveScale);
            break;

          case 'apply-rotation':
            userImage.applyRotation(event.payload.absoluteRotation, responsiveScale);
            break;

          case 'apply-pan':
            userImage.applyOffsets({ x: event.payload.x, y: event.payload.y }, responsiveScale);
            break;

          case 'reset':
            // Reset di tutti gli stati temporanei a null (stato di quiete)
            tempPanSV.value = null;
            tempZoomSV.value = null;
            tempRotationSV.value = null;
            break;
        }
      },
      [userImage.id], // Re-subscribe quando cambia userImageId
    );

    // Creazione del valore del context
    const contextValue: UserImageTransformContextProps = {
      tempPanSV,
      tempZoomSV,
      tempRotationSV,
      resetTempState,
    };

    return <UserImageTransformContext.Provider value={contextValue}>{children}</UserImageTransformContext.Provider>;
  },
);

export default UserImageTransformProvider;
